/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import cx from "classnames";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { usePlayerComponents } from "../../../../shared/context/PlayerComponentsContext";
import { useOutsideClick } from "../../../../shared/hooks/useOutsideClick";

import { ToggleVisibilityProvider } from "./ToggleVisibilityContext";

import "./ToggleButton.scss";

type ToggleButtonProps = {
  variant?: "primary" | "secondary" | "tertiary";
  icon?: ReactNode;
  text?: ReactNode;
  tooltip?: string;
  tooltipOffset?: number;
  iconOnly?: boolean;
  onVisibilityChange?: (value: boolean) => void;
  children?: ReactNode;
};

export const ToggleButton = (props: ToggleButtonProps) => {
  const { icon, text, onVisibilityChange, tooltip, children, ...rest } = props;

  const { Button } = usePlayerComponents();

  const [visible, setVisible] = useState<boolean>(false);

  const controlRef = useRef<HTMLDivElement>(null);

  useOutsideClick(controlRef, () => visible && setVisible(false));

  const onToggleVisibility = useCallback(() => setVisible((prev) => !prev), []);

  const contentClasses = cx("ToggleButton__Content", {
    "ToggleButton__Content--open": visible,
  });

  useEffect(() => onVisibilityChange?.(visible), [onVisibilityChange, visible]);

  return (
    <div className="ToggleButton" ref={controlRef}>
      <div>
        <Button
          onClick={onToggleVisibility}
          tooltip={!visible ? tooltip : ""}
          forceHoverStyle={visible}
          aria-label={tooltip}
          aria-expanded={visible}
          {...rest}
        >
          {icon}
          {text}
        </Button>
      </div>
      <div className={contentClasses}>
        <ToggleVisibilityProvider
          visible={visible}
          toggleVisibility={onToggleVisibility}
        >
          {children}
        </ToggleVisibilityProvider>
      </div>
    </div>
  );
};
